/**
 * MyActivity.tsx — the pharmacist's OWN recorded actions.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: pharmacy dispensing lane
 * (2026-07-29).
 *
 * WHY it exists at all: a dispensing record is only useful to the professional
 * it names if they can see it. This is the pharmacy mirror of the clinician's
 * `MyActivityLog` — same idea, same shape on screen, deliberately not the same
 * component because the two read different services with different scoping
 * rules and merging them would mean one component holding two role's worth of
 * knowledge.
 *
 * SELF-SCOPED WITH NO PARAMETER. Unlike the clinician read, which sends
 * `actorUserId=self` explicitly, this route derives the actor from the verified
 * principal server-side. There is no actor to send, so the read cannot be
 * widened from the browser at all — the strongest version of the same rule.
 *
 * PHI-free (R19): ids, action enums and timestamps, plus the reason a rejection
 * recorded. Money-free: the pharmacy audit vocabulary contains no money action,
 * so no money-adjacent row can reach this surface.
 */
import type { CSSProperties } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../api/client';
import { getPharmacyActivity } from '../api/pharmacy';
import type { PharmacyActivityEntry } from '../api/types';
import { formatUtcDateTime } from '../lib/format';
import { Alert, Card, ChevronRightIcon, ErrorState, Help } from '../ui/kit';
import type { BadgeTone } from '../ui/kit';
import { SkeletonCard } from '../ui/Skeleton';
import { SectionTitle } from '../ui/sections';

const ACTION_LABEL: Record<string, string> = {
  order_dispensed: 'Order dispensed',
  order_dispatched: 'Order dispatched',
  order_dispense_rejected: 'Dispense rejected',
  prescription_downloaded: 'Prescription downloaded',
};

const ACTION_TONE: Record<string, BadgeTone> = {
  order_dispensed: 'positive',
  order_dispatched: 'positive',
  order_dispense_rejected: 'danger',
  prescription_downloaded: 'muted',
};

const ACTION_ACCENT: Record<string, string> = {
  order_dispensed: 'var(--gps-primary)',
  order_dispatched: 'var(--gps-primary)',
  order_dispense_rejected: 'var(--gps-danger-text)',
  prescription_downloaded: 'var(--gps-muted)',
};

const PILL_TOKENS: Record<BadgeTone, { bg: string; text: string }> = {
  neutral: { bg: 'var(--gps-primary-light)', text: 'var(--gps-green-text)' },
  positive: { bg: 'var(--gps-primary-light)', text: 'var(--gps-green-text)' },
  pending: { bg: 'var(--gps-warn-bg)', text: 'var(--gps-warn-text)' },
  muted: { bg: 'var(--gps-surface-2)', text: 'var(--gps-muted)' },
  danger: { bg: 'var(--gps-danger-bg)', text: 'var(--gps-danger-text)' },
};

function actionLabel(action: string): string {
  return ACTION_LABEL[action] ?? action.replace(/_/g, ' ');
}

function actionTone(action: string): BadgeTone {
  return ACTION_TONE[action] ?? 'neutral';
}

function actionAccent(action: string): string {
  return ACTION_ACCENT[action] ?? 'var(--gps-border-strong, var(--gps-border))';
}

function ActivityPill({ tone, children }: { tone: BadgeTone; children: string }) {
  const t = PILL_TOKENS[tone];
  return (
    <span
      style={{
        display: 'inline-block',
        background: t.bg,
        color: t.text,
        fontSize: '12px',
        fontWeight: 600,
        letterSpacing: '0.01em',
        padding: '4px 10px',
        borderRadius: 'var(--gps-r-pill)',
        lineHeight: 1.3,
      }}
    >
      {children}
    </span>
  );
}

function ActivityRow({
  entry,
  onOpenOrder,
}: {
  entry: PharmacyActivityEntry;
  onOpenOrder?: (orderCaseId: string, orderRef: string) => void;
}) {
  const label = actionLabel(entry.action);
  const tone = actionTone(entry.action);
  const accent = actionAccent(entry.action);
  const interactive = Boolean(onOpenOrder);

  const rowStyle: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: interactive ? '1fr auto' : '1fr',
    alignItems: 'center',
    gap: '12px',
    width: '100%',
    textAlign: 'left',
    border: '1px solid var(--gps-border)',
    borderLeft: `3px solid ${accent}`,
    borderRadius: 'var(--gps-r-md)',
    padding: '14px 16px',
    background: 'var(--gps-surface)',
    cursor: interactive ? 'pointer' : 'default',
    transition: 'background 120ms ease, border-color 120ms ease, box-shadow 120ms ease',
  };

  const body = (
    <>
      <span style={{ display: 'grid', gap: '6px', minWidth: 0 }}>
        <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
          <ActivityPill tone={tone}>{label}</ActivityPill>
          <span
            style={{
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: '13px',
              fontWeight: 600,
              color: 'var(--gps-text)',
              letterSpacing: '0.02em',
            }}
          >
            {entry.orderRef}
          </span>
        </span>
        <span style={{ fontSize: '12.5px', color: 'var(--gps-muted)' }}>
          {formatUtcDateTime(entry.occurredAt)}
        </span>
        {entry.reason ? (
          <span
            style={{
              display: 'block',
              marginTop: '2px',
              padding: '8px 10px',
              borderRadius: 'var(--gps-r-sm)',
              background: 'var(--gps-surface-2)',
              fontSize: '13px',
              color: 'var(--gps-text)',
              lineHeight: 1.45,
            }}
          >
            <span style={{ fontWeight: 600, color: 'var(--gps-muted)' }}>Reason: </span>
            {entry.reason}
          </span>
        ) : null}
      </span>
      {interactive ? (
        <ChevronRightIcon size={16} />
      ) : null}
    </>
  );

  if (!interactive) {
    return (
      <li style={rowStyle} aria-label={`${label}, order ${entry.orderRef}`}>
        {body}
      </li>
    );
  }

  return (
    <li>
      <button
        type="button"
        onClick={() => onOpenOrder?.(entry.orderCaseId, entry.orderRef)}
        aria-label={`Open order ${entry.orderRef} — ${label}`}
        style={rowStyle}
        onMouseEnter={(event) => {
          event.currentTarget.style.background = 'var(--gps-primary-light)';
          event.currentTarget.style.borderColor = 'var(--gps-primary)';
          event.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.06)';
        }}
        onMouseLeave={(event) => {
          event.currentTarget.style.background = 'var(--gps-surface)';
          event.currentTarget.style.borderColor = 'var(--gps-border)';
          event.currentTarget.style.borderLeftColor = accent;
          event.currentTarget.style.boxShadow = 'none';
        }}
        onFocus={(event) => {
          event.currentTarget.style.outline = '2px solid var(--gps-primary)';
          event.currentTarget.style.outlineOffset = '2px';
        }}
        onBlur={(event) => {
          event.currentTarget.style.outline = 'none';
        }}
      >
        {body}
      </button>
    </li>
  );
}

export function MyActivity({ onOpenOrder }: { onOpenOrder?: (orderCaseId: string, orderRef: string) => void }) {
  const [entries, setEntries] = useState<PharmacyActivityEntry[] | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error' | 'unavailable'>('loading');

  const load = useCallback(() => {
    setStatus('loading');
    getPharmacyActivity()
      .then((response) => {
        setEntries(response.items);
        setStatus('ready');
      })
      .catch((error: unknown) => {
        setStatus(error instanceof ApiError && error.status === 404 ? 'unavailable' : 'error');
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <>
      <SectionTitle>My activity</SectionTitle>
      <Help>Everything recorded against you — open an order to see the full dispensing view.</Help>
      <Card>
        {status === 'loading' ? (
          <SkeletonCard lines={4} label="Loading your activity…" />
        ) : status === 'unavailable' ? (
          <Alert tone="info" role="status">
            Your activity record isn&rsquo;t available on this screen yet. Every dispense, rejection and
            dispatch is still being recorded against you by name — this is where you&rsquo;ll be able to read
            it back.
          </Alert>
        ) : status === 'error' || !entries ? (
          <ErrorState message="Sorry — we could not load your activity." onRetry={load} />
        ) : entries.length === 0 ? (
          <Help style={{ marginBottom: 0 }}>Nothing recorded yet — this fills in as you dispense.</Help>
        ) : (
          <ol
            aria-label="My recorded actions"
            style={{
              listStyle: 'none',
              margin: 0,
              padding: 0,
              display: 'grid',
              gap: '10px',
            }}
          >
            {entries.map((entry) => (
              <ActivityRow key={entry.auditId} entry={entry} onOpenOrder={onOpenOrder} />
            ))}
          </ol>
        )}
      </Card>
    </>
  );
}
